import { useCallback, useEffect, useRef, useState } from 'react';

import type { IDataRoomFileProgress } from './types';

const PROGRESS_FLUSH_MS = 200;

/** The per-file rows of a data room. Rows change in a ref and are drawn at most every 200 ms, because
 * progress events arrive per chunk of every part in flight. */
export const useDataRoomFileRows = () => {
  const [fileRows, setFileRows] = useState<IDataRoomFileProgress[]>([]);
  const rowsRef = useRef(new Map<string, IDataRoomFileProgress>());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    },
    []
  );

  const updateRow = useCallback(
    (
      fileId: string,
      patch: Partial<IDataRoomFileProgress>,
      isUrgent = false
    ) => {
      const row = rowsRef.current.get(fileId);
      if (row) rowsRef.current.set(fileId, { ...row, ...patch });
      if (flushTimerRef.current && !isUrgent) return;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(
        () => {
          flushTimerRef.current = null;
          setFileRows([...rowsRef.current.values()]);
        },
        isUrgent ? 0 : PROGRESS_FLUSH_MS
      );
    },
    []
  );

  /** Adds rows (a new set replaces the old one) and draws them at once. */
  const showRows = useCallback(
    (rows: readonly IDataRoomFileProgress[], isNewSet: boolean) => {
      if (isNewSet) rowsRef.current = new Map();
      rows.forEach((row) => rowsRef.current.set(row.fileId, row));
      setFileRows([...rowsRef.current.values()]);
    },
    []
  );

  const removeRow = useCallback((fileId: string) => {
    rowsRef.current.delete(fileId);
    setFileRows([...rowsRef.current.values()]);
  }, []);

  return { fileRows, rowsRef, updateRow, showRows, removeRow };
};
